import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import { updateData } from "../../store/slices/miscSlice";
import axios from "axios";
import host from "../../global.jsx";
import { candidateAuthHeader } from "../../utils/candidateAuth";
import { checkInterviewState } from "./Interviews/services/interviewService";
import {
  BrainCog,
  Code2,
  Users,
  Rocket,
  Trophy,
  Timer,
  Lock,
  CheckCircle,
  ChevronRight,
  Target,
  Save,
  Handshake,
} from "lucide-react";
import { toast } from "react-toastify";
import BrandingFooter from "../../components/shared/BrandingFooter.jsx";
import { useTheme } from "../../hooks/useTheme.js";

export default function Component() {
  const navigate = useNavigate();
  const { interview_id } = useParams();
  const [mcqStatus, setMCQStatus] = useState("Incomplete");
  const [codingStatus, setCodingStatus] = useState("Incomplete");
  const [interviewStatus, setInterviewStatus] = useState("Incomplete");
  const [mcqTimeLimit, setMCQTimeLimit] = useState(0);
  const [codingTimeLimit, setCodingTimeLimit] = useState(0);
  const [interviewTimeLimit, setInterviewTimeLimit] = useState(0);
  const [typingStatus, setTypingStatus] = useState("Incomplete");
  const [typingTimeLimit, setTypingTimeLimit] = React.useState("");
  const [behavioralStatus, setBehavioralStatus] = useState("Incomplete");
  const [behavioralTimeLimit, setBehavioralTimeLimit] = useState(0);
  const [assessmentSequence, setAssessmentSequence] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isCanceled, setIsCanceled] = useState(false);
  const whitelabelEnabled = useSelector((state) => state.miscSlice.whitelabelEnabled);
  const [candidateFormStatus, setCandidateFormStatus] = useState("Incomplete");
  const dispatch = useDispatch();
  const {theme } = useTheme();

  //  const [whitelabelEnabled , setisWhiteLabelingEnable] = useState(JSON.parse(localStorage.getItem("isWhite")))


  const totalAssessments = [
    mcqStatus,
    codingStatus,
    interviewStatus,
  , typingStatus, behavioralStatus].filter((status) => status !== "NA").length;
  const progress = [
    candidateFormStatus === "Complete",
    mcqStatus === "Complete" || mcqStatus === "Passed",
    codingStatus === "Complete",
    typingStatus === "Complete",
    interviewStatus === "Complete" || interviewStatus === "Passed",
    behavioralStatus === "Complete",
  ].filter(Boolean).length;

  useEffect(() => {
    const checkStatus = async () => {
      setIsLoading(true);
      try {
        const response = await axios.get(
          `${host}/check_test_progress/${interview_id}`,
          { headers: candidateAuthHeader(interview_id) }
        );
        if (response) {
          const {
            mcq_status, coding_status, typing_status, interview_status, behavioral_status,
            mcq_time_limit, coding_time_limit, interview_time_limit, typing_time_limit, behavioral_time_limit,
            assessment_sequence, candidate_form_status,
          } = response.data;
          setMCQStatus(mcq_status);
          setCodingStatus(coding_status);
          setTypingStatus(typing_status);
          setInterviewStatus(interview_status);
          setBehavioralStatus(behavioral_status);
          setCandidateFormStatus(candidate_form_status);
          setMCQTimeLimit(mcq_time_limit);
          setCodingTimeLimit(coding_time_limit);
          setInterviewTimeLimit(interview_time_limit);
          setTypingTimeLimit(typing_time_limit);
          setBehavioralTimeLimit(behavioral_time_limit);
          setAssessmentSequence(assessment_sequence);
        }
      } catch (error) {
        if (error.response?.status === 403) {
          setIsCanceled(true);
        } else if (error.response?.status === 401) {
          // No candidate token in this tab (new tab / expired) — back to OTP.
          navigate(`/candidate/${interview_id}/scheduletest`);
        } else {
          console.error(error);
        }
      } finally {
        setIsLoading(false);
      }
    };
    checkStatus();
  }, [interview_id]);

  const navigateMCQtest = async () => {
    try {
      const currentIndex = assessmentSequence.indexOf("mcq");
      const previousType =
        currentIndex > 0 ? assessmentSequence[currentIndex - 1] : null;

      const canStartMCQ =
        !previousType ||
        (previousType === "candidate_form"
          ? ["Complete", "NA"].includes(candidateFormStatus)
          : previousType === "coding"
          ? ["Complete", "NA"].includes(codingStatus)
          : previousType === "interview"
          ? ["Complete", "NA"].includes(interviewStatus)
          : previousType === "typing"
          ? ["Complete", "NA"].includes(typingStatus)
          : previousType === "behavioral"
          ? ["Complete", "NA"].includes(behavioralStatus)
          : true);

      if (canStartMCQ && mcqStatus === "Incomplete") {
        localStorage.setItem(`timeLeft_${interview_id}_mcq`, mcqTimeLimit * 60);
        setTimeout(() => {
          navigate(`/candidate/${interview_id}/mcqinstructions`);
        }, 1000);
      } else if (!canStartMCQ) {
        toast.warn("Please complete the previous assessment first!");
      } else {
        toast.warn("Knowledge Test is already completed!");
      }
    } catch (error) {
      console.error(error);
    }
  };

  const navigateCodingtest = async () => {
    try {
      const currentIndex = assessmentSequence.indexOf("coding");
      const previousType =
        currentIndex > 0 ? assessmentSequence[currentIndex - 1] : null;

      const canStartCoding =
        !previousType ||
        (previousType === "candidate_form"
          ? ["Complete", "NA"].includes(candidateFormStatus)
          : previousType === "mcq"
          ? ["Passed", "NA"].includes(mcqStatus)
          : previousType === "interview"
          ? ["Complete", "NA"].includes(interviewStatus)
          : previousType === "typing"
          ? ["Complete", "NA"].includes(typingStatus)
          : previousType === "behavioral"
          ? ["Complete", "NA"].includes(behavioralStatus)
          : true);

      if (canStartCoding && codingStatus !== "Complete") {
        localStorage.setItem(
          `timeLeft_${interview_id}_coding`,
          codingTimeLimit * 60
        );
        setTimeout(() => {
          navigate(`/candidate/${interview_id}/codinginstructions`);
        }, 1000);
      } else if (!canStartCoding) {
        toast.warn("Please complete the previous assessment first!");
      } else {
        toast.error("Coding Test is already completed!");
      }
    } catch (error) {
      console.error("Error: ", error);
    }
  };

  const navigateTyping = async () => {
    try {
      const currentIndex = assessmentSequence.indexOf('typing');
      const previousType = currentIndex > 0 ? assessmentSequence[currentIndex - 1] : null;

      const canStartTyping =
        !previousType ||
        (previousType === "candidate_form"
          ? ["Complete", "NA"].includes(candidateFormStatus)
          : previousType === "mcq"
          ? ["Passed", "NA"].includes(mcqStatus)
          : previousType === "coding"
          ? ["Complete", "NA"].includes(codingStatus)
          : previousType === "interview"
          ? ["Complete", "NA"].includes(interviewStatus)
          : previousType === "behavioral"
          ? ["Complete", "NA"].includes(behavioralStatus)
          : true);

      if (canStartTyping && typingStatus !== "Complete") {
        localStorage.setItem(
          `timeLeft_${interview_id}_typing`,
          typingTimeLimit * 60
        );
        setTimeout(() => {
          navigate(`/candidate/${interview_id}/typingtestinstructions`);
        }, 1000);
      } else if (!canStartTyping) {
        toast.warn("Please complete the previous assessment first!");
      } else {
        toast.error("Typing Test is already completed!");
      }
    } catch (error) {
      console.error("Error: ", error);
    }
  };

  const navigateCandidateForm = () => {
    if (candidateFormStatus !== "Complete") {
      navigate(`/candidate/${interview_id}/candidate-form`);
    } else {
      toast.warn("Candidate information form is already completed!");
    }
  };

  const navigateInterview = async () => {
    try {
      const currentIndex = assessmentSequence.indexOf("interview");
      const previousType =
        currentIndex > 0 ? assessmentSequence[currentIndex - 1] : null;

      const canStartInterview =
        !previousType ||
        (previousType === "candidate_form"
          ? ["Complete", "NA"].includes(candidateFormStatus)
          : previousType === "mcq"
          ? ["Passed", "NA"].includes(mcqStatus)
          : previousType === "coding"
          ? ["Complete", "NA"].includes(codingStatus)
          : previousType === "typing"
          ? ["Complete", "NA"].includes(typingStatus)
          : previousType === "behavioral"
          ? ["Complete", "NA"].includes(behavioralStatus)
          : true);

      if (canStartInterview) {
        // InterviewPortal is self-sufficient on entry — it checks resume state,
        // re-requests camera/mic/fullscreen itself, and shows its own
        // "Welcome back" screen — so a candidate who already started doesn't
        // need to sit through instructions/consent again. currentQuestionIndex
        // === -1 is the same "never started" signal useInterviewFlow uses to
        // decide between a fresh start and a resume.
        // Scoped to non-live-avatar interviews only: LiveAvatarFullModePortal
        // has no equivalent self-sufficient resume/permission handling, so
        // skipping instructions there isn't known-safe — leave that path
        // untouched.
        try {
          const configResponse = await fetch(`${host}/get_configurations_wo_auth/${interview_id}`);
          const config = await configResponse.json();
          if (!config.Live_Avatar_Enabled) {
            const { data } = await checkInterviewState(interview_id);
            if (data && data.currentQuestionIndex !== -1 && data.currentQuestionIndex !== undefined) {
              navigate(`/candidate/interview/${interview_id}`);
              return;
            }
          }
        } catch (e) {
          // Missing/invalid candidate token (e.g. a different browser/tab than
          // the one that verified OTP) -- send back to re-verify instead of
          // falling through to instructions, which would wrongly replay the
          // greeting for someone who may already be mid-interview.
          if (e.response?.status === 401) {
            navigate(`/candidate/${interview_id}/scheduletest`, { replace: true });
            return;
          }
          console.error("Error checking interview/resume state, falling back to instructions:", e);
        }
        navigate(`/candidate/${interview_id}/interviewinstructions`);
      } else {
        toast.warn("Please complete the previous assessment first!");
      }
    } catch (error) {
      console.error("Error: ", error);
    }
  };

  const navigateBehavioraltest = async () => {
    try {
      const currentIndex = assessmentSequence.indexOf("behavioral");
      const previousType = currentIndex > 0 ? assessmentSequence[currentIndex - 1] : null;

      const canStartBehavioral =
        !previousType ||
        (previousType === "candidate_form"
          ? ["Complete", "NA"].includes(candidateFormStatus)
          : previousType === "mcq"
          ? ["Passed", "NA"].includes(mcqStatus)
          : previousType === "coding"
          ? ["Complete", "NA"].includes(codingStatus)
          : previousType === "typing"
          ? ["Complete", "NA"].includes(typingStatus)
          : previousType === "interview"
          ? ["Complete", "NA"].includes(interviewStatus)
          : true);

      if (canStartBehavioral && behavioralStatus !== "Complete") {
        setTimeout(() => {
          navigate(`/candidate/${interview_id}/behavioralinstructions`);
        }, 1000);
      } else if (!canStartBehavioral) {
        toast.warn("Please complete the previous assessment first!");
      } else {
        toast.error("Behavioural Test is already completed!");
      }
    } catch (error) {
      console.error("Error: ", error);
    }
  };

  const SkeletonCardComponent = React.memo(() => {
    const bgContainer = theme === 'light' ? "bg-gray-200" : "bg-gray-800";
    const bgSkeleton = theme === 'light' ? "bg-gray-300" : "bg-gray-700";

    return (
      <div
        className={`relative rounded-3xl transition-all duration-500 ${bgContainer}`}
      >
        <div className="p-4 pl-10 h-full">
          <div className="flex items-start justify-between gap-10">
            <div className="flex-1">
              <div className="flex items-center gap-4 mb-4">
                <div
                  className={`p-4 rounded-2xl ${bgSkeleton} animate-pulse w-16 h-16`}
                ></div>

                <div className="space-y-2">
                  <div
                    className={`h-6 ${bgSkeleton} rounded animate-pulse w-40`}
                  ></div>
                  <div
                    className={`h-4 ${bgSkeleton} rounded animate-pulse w-24`}
                  ></div>
                </div>
              </div>

              <div
                className={`h-4 ${bgSkeleton} rounded animate-pulse w-full mb-2`}
              ></div>
            </div>
          </div>
        </div>
      </div>
    );
  });

  const renderAssessmentCard = (type, index) => {
    if (isLoading) {
      return <SkeletonCardComponent key={`skeleton-${index}`} />;
    }

    const validAssessments = assessmentSequence.filter(assessType => {
      if (assessType === 'mcq') return mcqStatus !== 'NA';
      if (assessType === 'coding') return codingStatus !== 'NA';
      if (assessType === 'interview') return interviewStatus !== 'NA';
      if (assessType === 'typing') return typingStatus !== 'NA';
      if (assessType === 'behavioral') return behavioralStatus !== 'NA';
      if (assessType === 'candidate_form') return candidateFormStatus !== 'NA';
      return false;
    });

    const stepNumber = validAssessments.indexOf(type) + 1;

    const cardProps = {
      mcq: {
        title: "Knowledge Assessment",
        icon: BrainCog,
        description: "Begin our adaptive assessment to test your knowledge.",
        onNavigate: navigateMCQtest,
        status: mcqStatus,
      },
      coding: {
        title: "Coding Challenge",
        icon: Code2,
        description:
          "Solve real-world problems to showcase your coding skills.",
        onNavigate: navigateCodingtest,
        status: codingStatus,
      },
      typing: {
        title: "Typing Test",
        icon: Target,
        description: "This is a timed typing assessment where your speed and accuracy will be measured. Type the given passage carefully to demonstrate your attention to detail and typing proficiency.",
        onNavigate: navigateTyping,
        status: typingStatus,
      },
      interview: {
        title: "Technical Interview",
        icon: Users,
        description: "This is an AI-powered technical interview round where the AI will ask you questions to evaluate your knowledge, problem-solving ability, and communication skills.",
        onNavigate: navigateInterview,
        status: interviewStatus,
      },
      candidate_form: {
        title: "Candidate Information",
        icon: Save,
        description: "Fill in your personal and professional details to get started.",
        onNavigate: navigateCandidateForm,
        status: candidateFormStatus,
      },
      behavioral: {
        title: "Behavioural Assessment",
        icon: Handshake,
        description: "Respond to real-world scenarios to showcase how you communicate, collaborate, and solve problems.",
        onNavigate: navigateBehavioraltest,
        status: behavioralStatus,
      },
    };

    const { title, icon, description, onNavigate, status } = cardProps[type];

    if (status === "NA") return null;

    return (
      <CardComponent
        key={type}
        title={title}
        icon={icon}
        description={description}
        onNavigate={onNavigate}
        status={status}
        stepNumber={stepNumber}
        validAssessments={validAssessments}
        mcqStatus={mcqStatus}
        codingStatus={codingStatus}
        typingStatus={typingStatus}
        interviewStatus={interviewStatus}
        candidateFormStatus={candidateFormStatus}
        behavioralStatus={behavioralStatus}
      />
    );
  };

  function formatTime(minutes) {
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    return hours > 0
      ? `${String(hours).padStart(2, "0")} H ${String(mins).padStart(
          2,
          "0"
        )} mins`
      : `${String(mins).padStart(2, "0")} mins`;
  }

  const CardComponent = React.memo(
    ({
      title,
      icon: Icon,
      onNavigate,
      description,
      status,
      stepNumber,
      validAssessments,
      mcqStatus,
      codingStatus,
      typingStatus,
      interviewStatus,
      candidateFormStatus,
      behavioralStatus,
    }) => {
      const isCompleted = status === "Complete" || status === "Passed";

      // 🌟 White-label palette
      const bgCardInactive = theme === 'light'
        ? "bg-white shadow-sm"
        : "bg-gray-700";
      const bgCardLocked = theme === 'light'
        ? "bg-gray-300"
        : "bg-gray-600";
      const textInactive = theme === 'light'
        ? "text-slate-900"
        : "text-gray-400";
      const textDim = theme === 'light' ? "text-gray-300" : "text-gray-300";
      const lineColor = theme === 'light' ? "bg-gray-300" : "bg-gray-200";

    const getCurrentType = () => {
      if (title === "Knowledge Assessment") return "mcq";
      if (title === "Coding Challenge") return "coding";
      if (title === "Typing Test") return "typing";
      if (title === "Candidate Information") return "candidate_form";
      if (title === "Behavioural Assessment") return "behavioral";

      return "interview";
    };

      const checkIsLocked = () => {
        const currentType = getCurrentType();
        const currentIndex = validAssessments.indexOf(currentType);

        if (currentIndex === 0) return false;

        const previousType = validAssessments[currentIndex - 1];
        if (!previousType) return false;

      return previousType === 'candidate_form' ? !['Complete', 'NA'].includes(candidateFormStatus) :
        previousType === 'mcq' ? !['Passed', 'NA'].includes(mcqStatus) :
        previousType === 'coding' ? !['Complete', 'NA'].includes(codingStatus) :
        previousType === 'typing' ? !['Complete', 'NA'].includes(typingStatus) :
        previousType === 'interview' ? !['Complete', 'NA'].includes(interviewStatus) :
        previousType === 'behavioral' ? !['Complete', 'NA'].includes(behavioralStatus) : false;
    };

    const shouldShowStartButton = () => {
      const currentType = getCurrentType();
      const currentIndex = validAssessments.indexOf(currentType);
      
      // For first assessment, show button only if not completed
      if (currentIndex === 0) {
        return !isCompleted; // Hide button if completed
      }
      
      // For subsequent assessments
      const previousType = validAssessments[currentIndex - 1];
      if (!previousType) return false;
      
      // Check previous assessment status
      const previousPassed =
        previousType === 'candidate_form' ? candidateFormStatus === 'Complete' :
        previousType === 'mcq' ? mcqStatus === 'Passed' :
        previousType === 'coding' ? codingStatus === 'Complete' :
        previousType === 'typing' ? typingStatus === 'Complete' :
        previousType === 'interview' ? interviewStatus === 'Complete' :
        previousType === 'behavioral' ? behavioralStatus === 'Complete' : false;

      // Show button only if previous is passed AND current is not completed
      return previousPassed && !isCompleted;
    };

      const isLocked = checkIsLocked();
      const isInProgress = !isLocked && !isCompleted;
      const showStartButton = shouldShowStartButton();

      return (
        <div className="relative">
          {/* STEP BADGE */}
          <div className="absolute -left-6 top-1/2 -translate-y-1/2 z-10">
            <div
              className={`w-12 h-12 rounded-full flex items-center justify-center text-md font-bold shadow-lg
                ${isCompleted
                  ? "bg-green-400 text-white"
                  : isInProgress
                  ? theme === 'light'
                    ? "bg-gradient-to-br from-[#2F64FF] to-[#FB54AE] text-white"
                    : "bg-gradient-to-br from-[#69007f] to-[#fc0987] text-white"
                  : theme === 'light' ? "bg-gray-200 text-gray-400" : "bg-gray-700 text-gray-500"
                }`}
            >
              {isLocked ? <Lock className="w-4 h-4" /> : isCompleted ? <CheckCircle className="w-5 h-5" /> : stepNumber}
            </div>
          </div>

          {/* CONNECTOR LINE */}
          {stepNumber < validAssessments.length && (
            <div
              className={`absolute -bottom-12 w-[2px] h-12 -z-10 ${isCompleted ? "bg-[#4ade80]" : lineColor}`}
              style={{ left: "19px" }}
            />
          )}

          {/* CARD */}
          <div
            className={`rounded-2xl transition-all duration-300 overflow-hidden
              ${isInProgress
                ? theme === 'light'
                  ? "bg-white border border-gray-200 shadow-lg"
                  : "glass-effect border border-white/10 shadow-2xl"
                : isCompleted
                ? theme === 'light'
                  ? "bg-white/60 border border-gray-100 shadow-sm"
                  : "glass-effect border border-white/5"
                : theme === 'light'
                ? "bg-gray-100 border border-gray-200"
                : "glass-effect border border-white/5 opacity-60"
              }`}
          >
            {/* Top accent bar for active card */}
            {isInProgress && (
              <div className="h-0.5 w-full bg-gradient-to-r from-pink-500 via-purple-500 to-blue-500" />
            )}

            <div className="p-5 sm:p-6 lg:p-8 lg:pl-12 flex flex-col sm:flex-row items-start sm:justify-between gap-4 lg:gap-10">
              {/* Icon + Info */}
              <div className="flex items-start gap-4 flex-1 min-w-0">
                <div className={`p-3 rounded-xl flex-shrink-0 ${
                  isInProgress
                    ? theme === 'light' ? "bg-pink-50" : "bg-pink-500/10"
                    : theme === 'light' ? "bg-gray-100" : "bg-white/5"
                }`}>
                  <Icon className={`w-6 h-6 ${isInProgress ? "text-pink-500" : theme === 'light' ? "text-gray-400" : "text-gray-500"}`} />
                </div>

                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap mb-1">
                    <h2 className={`text-base font-bold tracking-tight ${
                      isInProgress
                        ? theme === 'light' ? "text-slate-900" : "text-white"
                        : theme === 'light' ? "text-gray-500" : "text-gray-500"
                    }`}>
                      {title}
                    </h2>
                    <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${
                      isCompleted
                        ? "bg-green-100 text-green-600"
                        : isInProgress
                        ? theme === 'light' ? "bg-pink-50 text-pink-600" : "bg-pink-500/10 text-pink-400"
                        : theme === 'light' ? "bg-gray-100 text-gray-400" : "bg-white/5 text-gray-500"
                    }`}>
                      {isCompleted ? "✓ Done" : isInProgress ? "Up Next" : "Locked"}
                    </span>
                  </div>
                  <p className={`text-sm leading-relaxed ${
                    isInProgress
                      ? theme === 'light' ? "text-gray-600" : "text-gray-400"
                      : theme === 'light' ? "text-gray-400" : "text-gray-600"
                  }`}>
                    {description}
                  </p>

                  {/* Duration pill */}
                  {(() => {
                    const currentType = getCurrentType();
                    const timeLimit =
                      currentType === 'mcq' ? mcqTimeLimit :
                      currentType === 'coding' ? codingTimeLimit :
                      currentType === 'typing' ? typingTimeLimit :
                      currentType === 'candidate_form' ? 5 :
                      currentType === 'behavioral' ? behavioralTimeLimit :
                      interviewTimeLimit;

                    if (!timeLimit) return null;

                    return (
                      <div className={`inline-flex items-center gap-1.5 mt-3 px-2.5 py-1 rounded-full text-xs font-medium w-fit ${
                        isInProgress
                          ? theme === 'light' ? "bg-pink-50 text-pink-600" : "bg-pink-500/10 text-pink-400"
                          : theme === 'light' ? "bg-gray-100 text-gray-400" : "bg-white/5 text-gray-500"
                      }`}>
                        
                        <span>Duration: {formatTime(timeLimit)}</span>
                      </div>
                    );
                  })()}
                </div>
              </div>

              {/* CTA Button */}
              {showStartButton && (
                <div className="flex-shrink-0 flex items-center sm:self-center self-start">
                  <button
                    onClick={onNavigate}
                    disabled={!isInProgress}
                    className="button"
                  >
                    <span className="flex gap-2 items-center">
                      <span className="font-bold">Start Now</span>
                      <ChevronRight className="w-4 h-4" />
                    </span>
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      );
    }
  );

  if (isCanceled) {
    return (
      <div className={`min-h-screen flex items-center justify-center px-8 ${theme === 'light' && "bg-[#FAF9F6]"}`}>
        <div className="text-center space-y-4 max-w-md">
          <Lock className="w-12 h-12 mx-auto text-red-400" />
          <h2 className="text-2xl font-semibold text-gray-900 dark:text-white">Interview Cancelled</h2>
          <p className="text-gray-500 dark:text-gray-400">
            This interview has been cancelled. Please contact your recruiter if you think this is a mistake.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div
      style={{ zoom: 0.9, height: "calc(100dvh / 0.9)" }}
      className={`overflow-y-auto px-8 pt-8 ${
        theme === 'light' && "bg-[#FAF9F6]"
      }`}
    >
      <div className="flex flex-wrap justify-center items-start gap-6 lg:gap-12 pb-8">
        {/* Left Column - Stats */}
        <div className="w-full lg:w-5/12 space-y-8 py-4">
          <div
            className={`p-6 rounded-3xl relative overflow-hidden cardPattern ${
              theme === 'light'
                ? "bg-white text-slate-900 shadow-sm"
                : " text-white shadow-xl"
            }`}
          >
            <div className="relative z-10 space-y-6 ">
              
              <div className="flex items-center gap-3">
                <Rocket className="w-6 h-6 text-pink-500" />
                <h2
                  className={`text-xl font-semibold ${
                    theme === 'light' ? "text-slate-900" : "text-white"
                  }`}
                >
                  Assessment Flow
                </h2>
              </div>

              <p className="text-md opacity-90">
                Complete each step in sequence to unlock the next challenge. Your progress is saved automatically.
              </p>
               <div className="w-full border-b border-gray-300"/>
              <div className="grid grid-cols-1 gap-4 ">
                {mcqStatus === "NA" ||
                codingStatus === "NA" ||
                mcqStatus === "Passed" ||
                codingStatus === "Complete" ||
                mcqStatus === "Incomplete" ? (
                  <>
                    <div
                      className={`backdrop-blur-lg rounded-2xl p-4 m-4 shadow-md ${
                        theme === 'light' && "border"
                      }`}
                    >
                      <div className="flex items-center gap-4">
                        {isLoading ? (
                          <div className="flex items-center gap-2">
                            <div
                              className={`w-8 h-8 ${
                                theme === 'light'
                                  ? "bg-gray-200"
                                  : "bg-gray-500"
                              } rounded-xl animate-pulse`}
                            ></div>
                            <div
                              className={`h-8  rounded animate-pulse w-12 ${
                                theme === 'light'
                                  ? "bg-gray-200"
                                  : "bg-gray-500"
                              }`}
                            ></div>
                          </div>
                        ) : (
                          <>
                            <Trophy className="w-6 h-6 mb-1" />
                            <div
                              className={`text-2xl font-semibold ${
                                theme === 'light'
                                  ? "text-gray-700"
                                  : "text-white"
                              }`}
                            >
                              {progress}/{assessmentSequence.length}
                            </div>
                          </>
                        )}
                      </div>
                      {isLoading ? (
                        <div
                          className={`h-4 ${
                            theme === 'light'
                              ? "bg-gray-200"
                              : "bg-gray-500"
                          } rounded animate-pulse w-32 mt-2`}
                        ></div>
                      ) : (
                        <div
                          className={`text-sm ${
                            theme === 'light'
                              ? "text-gray-600"
                              : "text-white "
                          } opacity-90 mt-2`}
                        >
                          {progress !== assessmentSequence.length ? (
                            <span>Progress</span>
                          ) : (
                            <span className={`${theme === 'light' ? 'text-black':'text-gray-400'} `}>
                              All assessments are complete!
                            </span>
                          )}
                        </div>
                      )}

                      <div className="w-full h-1 mt-4 bg-gray-300 rounded-full">
                        <div
                          className="h-full bg-green-500 rounded-full"
                          style={{
                            width: `${
                              (progress / assessmentSequence.length) * 100
                            }%`,
                            display: `${isLoading ? "none" : "block"}`,
                          }}
                        ></div>
                      </div>
                    </div>
                  </>
                ) : (
                  <div className="max-w-md mx-auto transform">
                    <div
                      className={`relative overflow-hidden rounded-2xl ${
                        theme === 'light'
                          ? " shadow-sm border"
                          : "shadow-xl"
                      }`}
                    >
                      {/* Gradient border effect using pseudo-element */}
                      <div className="absolute inset-0" />

                      {/* Inner content with glass effect */}
                      <div className="relative m-[2px] p-8 rounded-2xl">
                        {/* Decorative elements */}
                        <div className="absolute top-0 left-0 w-24 h-24 bg-[#3ECFDA] rounded-full opacity-10 blur-2xl" />
                        <div className="absolute bottom-0 right-0 w-32 h-32 bg-[#0952DA] rounded-full opacity-10 blur-2xl" />

                        {/* Content */}
                        <div className="relative z-10 text-center">
                          <h2 className="text-lg font-bold text-white">
                            Unfortunately, Your Journey Ends Here!
                          </h2>
                          <p className="mt-4 text-xs text-white-400 opacity-75  text-center">
                            Keep growing! Every end is a new beginning ✨
                          </p>
                          <div className="mt-4 h-[2px] w-16 mx-auto bg-gradient-to-r from-[#3ECFDA] to-[#0952DA] rounded-full" />
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Decorative elements */}
            <div className="absolute top-0 right-0 w-64 h-64 bg-white/10 rounded-full blur-3xl -translate-y-1/2 translate-x-1/2" />
            <div className="absolute -bottom-8 -left-8 w-48 h-48 bg-white/10 rounded-full blur-2xl" />
          </div>
              
              <div className="w-full border-b border-gray-200"/>
          {/* Progress Tracker */}
          <div
            className={`rounded-3xl  p-6 ${
              theme === 'light'
                ? " shadow-sm border backdrop-blur-sm  bg-white"
                : "shadow-lg cardPattern backdrop-blur-sm "
            }`}
          >
            <div className="flex items-center gap-3 mb-6">
              <Target className="w-6 h-6 text-pink-500" />
              <h2
                className={`text-xl font-semibold ${
                  theme === 'light' ? "text-slate-900" : "text-white"
                }`}
              >
                Round Details
              </h2>
            </div>
            <div className="space-y-4">
              {isLoading ? (
                <div className="space-y-4">
                  {[1, 2].map((i) => (
                    <div key={i} className="flex items-center gap-2">
                      <div className="flex-1">
                        <div
                          className={`h-4 rounded animate-pulse w-32 ${
                            theme === 'light'
                              ? "bg-gray-200"
                              : "bg-gray-700"
                          }`}
                        ></div>
                      </div>
                      <div className="flex items-center gap-1">
                        <div
                          className={`w-4 h-4  rounded animate-pulse ${
                            theme === 'light'
                              ? "bg-gray-200"
                              : "bg-gray-700"
                          }`}
                        ></div>
                        <div
                          className={`h-3 rounded animate-pulse w-16 ${
                            theme === 'light'
                              ? "bg-gray-200"
                              : "bg-gray-700"
                          }`}
                        ></div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                assessmentSequence.map((type) => {
                  const assessmentDetails = {
                    mcq: {
                      title: "Knowledge Assessment",
                      timeLimit: mcqTimeLimit,
                      status: mcqStatus,
                    },
                    coding: {
                      title: "Coding Challenge",
                      timeLimit: codingTimeLimit,
                      status: codingStatus,
                    },
                    typing: {
                      title: "Typing Test",
                      timeLimit: typingTimeLimit,
                      status: typingStatus,
                    },
                    interview: {
                      title: "Technical Interview",
                      timeLimit: interviewTimeLimit,
                      status: interviewStatus,
                    },
                    candidate_form: {
                      title: "Candidate Information",
                      timeLimit: null,
                      status: candidateFormStatus,
                    },
                    behavioral: {
                      title: "Behavioural Assessment",
                      timeLimit: behavioralTimeLimit,
                      status: behavioralStatus,
                    },
                  };

                  if (type === 'candidate_form') return null;

                  const { title, timeLimit, status } = assessmentDetails[type];

                  // Skip rendering if status is "NA"
                  if (status === "NA") return null;

                  return (
                    <div
                      key={type}
                      className={`flex items-center justify-between px-4 py-3 rounded-xl ${
                        theme === 'light'
                          ? "bg-gray-50 border border-gray-100"
                          : "bg-white/[0.03] border border-white/5"
                      }`}
                    >
                      <div className={`text-sm font-medium ${theme === 'light' ? "text-gray-700" : "text-gray-200"}`}>
                        {title}
                      </div>
                      <div className={`flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium ${
                        theme === 'light'
                          ? "bg-pink-50 text-pink-600"
                          : "bg-pink-500/10 text-pink-400"
                      }`}>
                        <Timer className="w-3 h-3" />
                        <span className="whitespace-nowrap">{formatTime(timeLimit)}</span>
                      </div>
                    </div>
                  );
                })
              )}
            </div>
          </div>
        </div>

        {/* Right Column - Assessment Cards */}
        <div className="w-full lg:w-6/12 space-y-8 relative pl-6 py-4">
          {isLoading
            ? Array.from({ length: 3 }).map((_, index) => (
                <SkeletonCardComponent key={`skeleton-${index}`} />
              ))
            : assessmentSequence.map((type, index) =>
                renderAssessmentCard(type, index)
              )}
        </div>
      </div>
      <BrandingFooter/>
    </div>
  );
}
